import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewChecked,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { GuarantorService } from '../../services/guarantor/guarantor.service';
import { ToastService } from '../../services/general/toast.service';
import { BrowserInfo, GuarantorInfo } from '../../interfaces/responses/guarantor.interface';

type FormStep = 'personal' | 'documents' | 'declaration';

@Component({
  selector: 'app-guarantor-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './guarantor-form.component.html',
  styleUrl: './guarantor-form.component.scss',
})
export class GuarantorFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _fb = inject(FormBuilder);
  private _guarantorService = inject(GuarantorService);
  private _toast = inject(ToastService);

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('ghanaCardInput') ghanaCardInput!: ElementRef<HTMLInputElement>;

  token: string = '';
  currentStep: FormStep = 'personal';
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  isSubmitted: boolean = false;
  guarantorInfo: GuarantorInfo | null = null;
  browserInfo: BrowserInfo | null = null;

  stream: MediaStream | null = null;
  isCapturing: boolean = false;
  capturedSelfie: string | null = null;
  cameraError: string = '';

  ghanaCardFile: File | null = null;
  ghanaCardPreview: string | null = null;

  signatureData: string | null = null;
  signatureRequired = false;
  signatureMode: 'draw' | 'upload' = 'draw';
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  private signaturePadReady = false;

  relationshipOptions = [
    { label: 'Spouse', value: 'spouse' },
    { label: 'Parent', value: 'parent' },
    { label: 'Sibling', value: 'sibling' },
    { label: 'Child', value: 'child' },
    { label: 'Colleague', value: 'colleague' },
    { label: 'Friend', value: 'friend' },
    { label: 'Other', value: 'other' },
  ];

  personalForm: FormGroup = this._fb.group({
    fullName: ['', [Validators.required]],
    ghanaCardNumber: ['', [Validators.required]],
    mobileNumber: ['', [Validators.required]],
    digitalAddress: ['', [Validators.required, Validators.pattern(/^[A-Z]{2}-\d+-\d+$/i)]],
    placeOfWork: ['', [Validators.required]],
    jobDetail: ['', [Validators.required]],
    netSalary: ['', [Validators.required, Validators.min(1)]],
    relationshipToBorrower: ['', [Validators.required]],
  });

  declarationForm: FormGroup = this._fb.group({
    declarationAccepted: [false, [Validators.requiredTrue]],
  });

  ngOnInit() {
    this._route.params.subscribe((params) => {
      this.token = params['token'] || '';
      if (this.token) {
        this.loadGuarantorInfo();
      } else {
        this._router.navigate(['/404']);
      }
    });
  }

  loadGuarantorInfo() {
    this.isLoading = true;

    this._guarantorService.getGuarantorByToken(this.token).subscribe({
      next: (info) => {
        this.isLoading = false;
        this.guarantorInfo = info;
        const already = info.guarantors?.find(g => g.token === this.token);
        if (already) {
          this.isSubmitted = true;
          return;
        }
        this.collectBrowserInfo();
      },
      error: (error: any) => {
        this.isLoading = false;
        if (error.status === 404) {
          this._router.navigate(['/404']);
        } else {
          this._toast.error('Failed to load form. Please try again.');
        }
      },
    });
  }

  ngAfterViewChecked() {
    if (this.currentStep === 'declaration' && !this.signaturePadReady && this.signatureCanvas?.nativeElement) {
      this.initSignaturePad();
      this.signaturePadReady = true;
    } else if (this.currentStep !== 'declaration') {
      this.signaturePadReady = false;
    }
  }

  initSignaturePad() {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.offsetWidth;
    const cssHeight = canvas.offsetHeight;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#073F63';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }

  onSignatureStart(event: PointerEvent) {
    this.isDrawing = true;
    const canvas = this.signatureCanvas.nativeElement;
    canvas.setPointerCapture(event.pointerId);
    const rect = canvas.getBoundingClientRect();
    this.lastX = event.clientX - rect.left;
    this.lastY = event.clientY - rect.top;
  }

  onSignatureMove(event: PointerEvent) {
    if (!this.isDrawing) return;
    const canvas = this.signatureCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(this.lastX, this.lastY);
    ctx.lineTo(x, y);
    ctx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  onSignatureEnd() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.signatureData = this.signatureCanvas.nativeElement.toDataURL('image/png');
    this.signatureRequired = false;
  }

  clearSignature() {
    this.signatureData = null;
    if (this.signatureMode === 'draw') {
      const canvas = this.signatureCanvas?.nativeElement;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
  }

  onSignatureUploaded(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this._toast.error('Only JPEG, PNG, or WebP images are allowed.');
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this._toast.error('Signature image too large. Maximum size is 2MB.');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      this.signatureData = e.target?.result as string;
      this.signatureRequired = false;
    };
    reader.readAsDataURL(file);
  }

  nextFromPersonal() {
    if (this.personalForm.invalid) {
      this.personalForm.markAllAsTouched();
      return;
    }
    this.currentStep = 'documents';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  nextFromDocuments() {
    if (!this.ghanaCardFile) {
      this._toast.error('Please upload your Ghana Card image.');
      return;
    }
    if (!this.capturedSelfie) {
      this._toast.error('Please capture your live photo.');
      return;
    }
    this.currentStep = 'declaration';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goBack() {
    if (this.currentStep === 'documents') this.currentStep = 'personal';
    else if (this.currentStep === 'declaration') this.currentStep = 'documents';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onGhanaCardSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 5 * 1024 * 1024) {
      this._toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    this.ghanaCardFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.ghanaCardPreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeGhanaCard() {
    this.ghanaCardFile = null;
    this.ghanaCardPreview = null;
    if (this.ghanaCardInput?.nativeElement) {
      this.ghanaCardInput.nativeElement.value = '';
    }
  }

  async startCamera() {
    try {
      this.cameraError = '';
      this.isCapturing = true;
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 960 }, facingMode: 'user' },
      });
      if (this.videoElement?.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;
        this.videoElement.nativeElement.play();
      }
    } catch (err: any) {
      const isSecurityError =
        !window.isSecureContext ||
        err?.name === 'NotSupportedError' ||
        err?.name === 'SecurityError';
      this.cameraError = isSecurityError
        ? 'Camera access requires a secure connection (HTTPS). Please contact support if this issue persists.'
        : 'Unable to access camera. Please grant camera permissions and try again.';
      this.isCapturing = false;
    }
  }

  capturePhoto() {
    if (!this.videoElement?.nativeElement || !this.canvasElement?.nativeElement) return;
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const vw = video.videoWidth || 640;
    const vh = video.videoHeight || 480;
    canvas.width = vw;
    canvas.height = vh;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.save();
    ctx.translate(vw, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, vw, vh);
    ctx.restore();
    this.capturedSelfie = canvas.toDataURL('image/jpeg', 0.92);
    this.stopCamera();
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.isCapturing = false;
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }
  }

  retakePhoto() {
    this.capturedSelfie = null;
    this.cameraError = '';
    setTimeout(() => this.startCamera(), 100);
  }

  submitForm() {
    if (this.declarationForm.invalid) {
      this.declarationForm.markAllAsTouched();
      return;
    }
    if (!this.ghanaCardFile || !this.capturedSelfie) {
      this._toast.error('Missing required documents. Please go back and upload them.');
      return;
    }
    if (!this.signatureData) {
      this.signatureRequired = true;
      return;
    }

    const v = this.personalForm.value;
    const formData = new FormData();
    formData.append('name', v.fullName);
    formData.append('ghanaCardNumber', v.ghanaCardNumber);
    formData.append('phone', v.mobileNumber);
    formData.append('digitalAddress', v.digitalAddress);
    formData.append('workPlace', v.placeOfWork);
    formData.append('workRole', v.jobDetail);
    formData.append('netSalary', String(parseFloat(v.netSalary)));
    formData.append('relationship', v.relationshipToBorrower);
    formData.append('loanReference', this.guarantorInfo?.reference ?? '');
    formData.append('token', this.token);
    formData.append('ghanaCardImage', this.ghanaCardFile, this.ghanaCardFile.name);
    formData.append('image', this.dataURLtoBlob(this.capturedSelfie), 'selfie.jpg');
    formData.append('signature', this.dataURLtoBlob(this.signatureData!), 'signature.png');

    this.isSubmitting = true;
    this._guarantorService.submitGuarantorForm(formData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.personalForm.reset();
        this.declarationForm.reset();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (error: any) => {
        this.isSubmitting = false;
        const serverMsg = error?.error?.message;
        const msg = serverMsg
          ? (Array.isArray(serverMsg) ? serverMsg[0] : serverMsg)
          : 'Submission failed. Please try again.';
        this._toast.error(msg);
      },
    });
  }

  hasError(form: FormGroup, field: string): boolean {
    const c = form.get(field);
    return !!(c?.invalid && (c.touched || c.dirty));
  }

  get stepNumber(): number {
    return { personal: 1, documents: 2, declaration: 3 }[this.currentStep];
  }

  get borrowerName(): string {
    if (!this.guarantorInfo) return '';
    const u = this.guarantorInfo.user;
    return [u.firstName, u.otherName, u.lastName].filter(Boolean).join(' ');
  }

  private dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  }

  collectBrowserInfo() {
    if (typeof window === 'undefined') return;
    const nav = window.navigator as any;
    this.browserInfo = {
      userAgent: nav.userAgent || '',
      platform: nav.platform || '',
      language: nav.language || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      cookieEnabled: nav.cookieEnabled ?? false,
      doNotTrack: nav.doNotTrack ?? null,
      referrer: document.referrer || '',
      deviceMemory: nav.deviceMemory,
      hardwareConcurrency: nav.hardwareConcurrency,
    };
  }

  getBrowserLabel(): string {
    if (!this.browserInfo) return '';
    const ua = this.browserInfo.userAgent;
    let browser = 'Unknown Browser';
    if (ua.includes('Edg/')) browser = 'Edge';
    else if (ua.includes('Chrome/')) browser = 'Chrome';
    else if (ua.includes('Firefox/')) browser = 'Firefox';
    else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';
    let os = 'Unknown OS';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    else if (ua.includes('Linux')) os = 'Linux';
    return `${browser} on ${os}`;
  }

  ngOnDestroy() {
    this.stopCamera();
  }
}
